/**
 * Where a popup sits over the map: beside the click, flipped to the other side of it where
 * it would run off the map, kept inside the map, and never taller or wider than the map
 * (longer content scrolls inside), and inside what the window shows of the map. The popup is
 * placed in screen pixels and does not follow the map, so keeping it inside is enough; the map
 * is never panned for it. It is placed again whenever its size changes (an image loading).
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Box } from '@mantine/core';

/** Space between the click and the popup, and between the popup and the map's edge. */
export const POPUP_OFFSET = 12;
export const POPUP_MARGIN = 8;

export interface Placement {
  left: number;
  top: number;
  maxWidth: number;
  maxHeight: number;
}

/** The part of the map the reader can see, in the map's pixels (a map scrolled partly off the window is cut to it). */
export interface Visible {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** One axis inside [lo, hi]: after the click if it fits, else before it, else as far in as the room allows. */
function along(at: number, size: number, lo: number, hi: number): number {
  const after = at + POPUP_OFFSET;
  if (after + size <= hi - POPUP_MARGIN) return after;
  const before = at - POPUP_OFFSET - size;
  if (before >= lo + POPUP_MARGIN) return before;
  return Math.max(lo + POPUP_MARGIN, hi - POPUP_MARGIN - size);
}

/**
 * The popup's place for a click at `point`, given its size and the map's; sizes of 0 (not measured
 * yet) keep it beside the click. `visible` narrows the room to what the window shows of the map.
 */
export function placePopup(point: { x: number; y: number }, size: { width: number; height: number }, map: { width: number; height: number }, visible?: Visible): Placement {
  const room = clampRoom(map, visible);
  const maxWidth = Math.max(0, room.right - room.left - 2 * POPUP_MARGIN);
  const maxHeight = Math.max(0, room.bottom - room.top - 2 * POPUP_MARGIN);
  if (!map.width || !map.height) return { left: point.x + POPUP_OFFSET, top: point.y + POPUP_OFFSET, maxWidth, maxHeight };
  const width = Math.min(size.width, maxWidth);
  const height = Math.min(size.height, maxHeight);
  return { left: along(point.x, width, room.left, room.right), top: along(point.y, height, room.top, room.bottom), maxWidth, maxHeight };
}

function clampRoom(map: { width: number; height: number }, visible?: Visible): Visible {
  const whole = { left: 0, top: 0, right: map.width, bottom: map.height };
  if (!visible) return whole;
  const cut = { left: Math.max(0, visible.left), top: Math.max(0, visible.top), right: Math.min(map.width, visible.right), bottom: Math.min(map.height, visible.bottom) };
  // A map not in the window at all (or a test without layout) keeps its own bounds.
  return cut.right - cut.left > 2 * POPUP_MARGIN && cut.bottom - cut.top > 2 * POPUP_MARGIN ? cut : whole;
}

/** What the window shows of `map`, in its own pixels. */
function visibleOf(map: HTMLElement): Visible | undefined {
  if (typeof window === 'undefined') return undefined;
  const r = map.getBoundingClientRect();
  if (!r.width || !r.height) return undefined;
  return { left: -r.left, top: -r.top, right: window.innerWidth - r.left, bottom: window.innerHeight - r.top };
}

export function PopupFrame({ point, testId, children }: { point: { x: number; y: number }; testId?: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<Placement>(() => placePopup(point, { width: 0, height: 0 }, { width: 0, height: 0 }));
  // Measured before paint, so the popup never shows in the wrong place first, and again whenever
  // the popup or the map changes size: an image loading inside it, the map maximised or resized.
  useLayoutEffect(() => {
    const el = ref.current;
    const map = el?.parentElement;
    if (!el || !map) return;
    const measure = () => {
      const next = placePopup(point, { width: el.offsetWidth, height: el.offsetHeight }, { width: map.clientWidth, height: map.clientHeight }, visibleOf(map));
      setPlace((prev) => (prev.left === next.left && prev.top === next.top && prev.maxWidth === next.maxWidth && prev.maxHeight === next.maxHeight ? prev : next));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    observer.observe(map);
    // The frame's own box is capped at the map; what grows inside it is its child.
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [point, children]);
  return (
    <Box
      ref={ref}
      data-testid={testId}
      style={{ position: 'absolute', left: place.left, top: place.top, zIndex: 3, maxWidth: place.maxWidth || undefined, maxHeight: place.maxHeight || undefined, overflowY: 'auto' }}
    >
      {children}
    </Box>
  );
}
